import React, { useState } from 'react';
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const IndividualContributions = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('1M');
  
  // Sample data - would come from your API
  const contributionData = {
    name: "Arjun Mehta",
    role: "AI Engineer",
    insights: [
      {
        project: "Airport Face Recognition",
        contribution: "Led thermal imaging integration initiative",
        impact: "Reduced false positives by 35%",
        date: "2024-02-10"
      },
      {
        project: "Pedestrian Detection",
        contribution: "Optimized temporal smoothing logic",
        impact: "Improved detection accuracy by 28%",
        date: "2024-02-08"
      }
    ],
    activityMetrics: [
      { date: '2024-02-01', commits: 8, reviews: 5, documentation: 3, collaboration: 12 },
      // ... more data points
    ],
    crossToolInsights: [
      {
        type: "High Impact",
        description: "Identified and resolved critical edge device optimization issues affecting multiple projects",
        tools: ["Slack", "Jira", "GitHub"],
        date: "2024-02-09"
      },
      {
        type: "Knowledge Share",
        description: "Documented thermal imaging integration approach, referenced by 3 other teams",
        tools: ["Notion", "Confluence"],
        date: "2024-02-07"
      }
    ]
  };

  // Add activity data for charts
  const activityData = [
    { date: '2024-02-01', commits: 8, reviews: 5, documentation: 3, collaboration: 12 },
    { date: '2024-02-02', commits: 6, reviews: 7, documentation: 2, collaboration: 8 },
    { date: '2024-02-03', commits: 10, reviews: 3, documentation: 4, collaboration: 15 },
    { date: '2024-02-04', commits: 5, reviews: 8, documentation: 5, collaboration: 10 },
    { date: '2024-02-05', commits: 7, reviews: 6, documentation: 3, collaboration: 13 }
  ];

  return (
    <div className="space-y-8">
      {/* Engineer Overview - Updated colors */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{contributionData.name}</h2>
            <p className="text-gray-600">{contributionData.role}</p>
          </div>
          <div className="flex space-x-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">92%</div>
              <div className="text-sm text-gray-600">Efficiency</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">85%</div>
              <div className="text-sm text-gray-600">Knowledge Share</div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Overview Chart */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Overview</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={activityData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="commits"
              stackId="1"
              stroke="#3B82F6"
              fill="#93C5FD"
            />
            <Area
              type="monotone"
              dataKey="reviews"
              stackId="1"
              stroke="#10B981"
              fill="#6EE7B7"
            />
            <Area
              type="monotone"
              dataKey="documentation"
              stackId="1"
              stroke="#8B5CF6"
              fill="#C4B5FD"
            />
            <Area
              type="monotone"
              dataKey="collaboration"
              stackId="1"
              stroke="#F59E0B"
              fill="#FCD34D"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Impact - Updated colors */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Impact</h3>
        <div className="space-y-4">
          {contributionData.insights.map((insight, idx) => (
            <div key={idx} className="border-l-4 border-blue-500 pl-4 bg-white">
              <div className="text-sm text-gray-600">{insight.project}</div>
              <div className="font-medium text-gray-900">{insight.contribution}</div>
              <div className="text-green-600 text-sm">{insight.impact}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Knowledge Synthesis - Updated colors */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Knowledge Synthesis</h3>
        <div className="space-y-6">
          {contributionData.crossToolInsights.map((insight, idx) => (
            <div key={idx} className="flex space-x-4 bg-gray-50 p-4 rounded-lg">
              <div className="flex-1">
                <div className="text-sm font-medium text-blue-600">{insight.type}</div>
                <p className="text-gray-700 mt-1">{insight.description}</p>
                <div className="flex space-x-2 mt-2">
                  {insight.tools.map(tool => (
                    <span key={tool} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Activity Timeline */}
      {/* Add interactive timeline showing cross-tool activity */}
    </div>
  );
};

export default IndividualContributions; 